/* global google */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import Sidebar from './Sidebar';
import { loadGoogleMaps } from '../googleMapsLoader';
import {
    buildItemKey,
    formatDistanceKm,
    getCapLocation,
    getItemAddress,
    normalizeCap,
    normalizeCapIndex,
} from '../utils/geoUtils';

const ITALY_CENTER = { lat: 41.913355, lng: 12.484130 };
const ITALY_ZOOM_DESKTOP = 6;
const ITALY_ZOOM_MOBILE = 6;
const SELECTED_ITEM_ZOOM = 16;

const PROXIMITY_SEARCH_CONFIG = {
    cap: {
        radiiKm: [3, 5, 10, 20, 50],
    },
    userLocation: {
        radiiKm: [3, 5, 10, 20, 50],
    }, 
};

const MARKER_CLUSTER_CONFIG = {
    enabled: true,
    maxZoom: 14,
    radius: 80,
    minPoints: 3,
    size: 44,
    maxSize: 62,
    color: '#e2454c',
    textColor: '#fff',
    borderColor: '#fff',
    borderWidth: 3,
};

const MAP_FEATURES = {
    fillColor: '#284664',
    strokeColor: '#fff',
    strokeWeight: 1,
    fillColorSelected: '#4477aa',
    fillOpacity: 1,
};

function normalizeColumnKey(key) {
    const normalizedKey = String(key || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const compactKey = normalizedKey.replace(/[\s_-]+/g, '');

    if (compactKey === 'tipologiastruttura') {
        return 'tipologia struttura';
    }

    if (compactKey === 'indirizzocompleto') {
        return 'indirizzo completo';
    }

    return normalizedKey;
}

function normalizeItem(rawItem) {
    const normalized = {};

    Object.keys(rawItem || {}).forEach(key => {
        normalized[normalizeColumnKey(key)] = rawItem[key];
    });

    if (!normalized['tipologia struttura'] && normalized.tipologia) {
        normalized['tipologia struttura'] = normalized.tipologia;
    }

    return normalized;
}

function getStructureType(item) {
    return item['tipologia struttura'] || item.tipologia || item.ente || '';
}

function getItemTitle(item) {
    return item.denominazione || getStructureType(item) || '';
}

function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, char => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
    }[char]));
}

function buildEmailLinks(email) {
    if (!email) return '';

    return String(email)
        .split(/[;,]/)
        .map(e => e.trim())
        .filter(Boolean)
        .map(e => `<a title="Invia una mail a ${escapeHtml(e)}" href="mailto:${encodeURIComponent(e)}" class="email-address">${escapeHtml(e)}</a>`)
        .join('');
}

function buildClusterSvg({ count, size }) {
    const escapedColor = escapeHtml(MARKER_CLUSTER_CONFIG.color);
    const escapedBorderColor = escapeHtml(MARKER_CLUSTER_CONFIG.borderColor);
    const radius = size / 2;
    const innerRadius = Math.max(radius - MARKER_CLUSTER_CONFIG.borderWidth, 0);

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <circle cx="${radius}" cy="${radius}" r="${radius}" fill="${escapedBorderColor}" opacity="0.95"/>
        <circle cx="${radius}" cy="${radius}" r="${innerRadius}" fill="${escapedColor}" opacity="0.92"/>
        <circle cx="${radius}" cy="${radius}" r="${Math.max(innerRadius - 7, 0)}" fill="${escapedColor}" opacity="0.72"/>
    </svg>`;
}

function createClusterRenderer() {
    return {
        render({ count, position }) {
            const computedSize = MARKER_CLUSTER_CONFIG.size + Math.log10(Math.max(count, 1)) * 8;
            const size = Math.round(Math.min(MARKER_CLUSTER_CONFIG.maxSize, computedSize));
            const svg = buildClusterSvg({ count, size });

            return new google.maps.Marker({
                position,
                icon: {
                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
                    scaledSize: new google.maps.Size(size, size),
                    anchor: new google.maps.Point(size / 2, size / 2),
                },
                label: {
                    text: String(count),
                    color: MARKER_CLUSTER_CONFIG.textColor,
                    fontSize: count >= 1000 ? '11px' : '13px',
                    fontWeight: '700',
                },
                zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
            });
        },
    };
}

function getInitialZoom() {
    return window.innerWidth > 600 ? ITALY_ZOOM_DESKTOP : ITALY_ZOOM_MOBILE;
}

function getDistanceKm(origin, destination) {
    const toRadians = value => (value * Math.PI) / 180;
    const earthRadiusKm = 6371;

    const originLat = Number(origin?.lat);
    const originLng = Number(origin?.lng);
    const destinationLat = Number(destination?.lat);
    const destinationLng = Number(destination?.lng);

    if (
        Number.isNaN(originLat) ||
        Number.isNaN(originLng) ||
        Number.isNaN(destinationLat) ||
        Number.isNaN(destinationLng)
    ) {
        return Number.POSITIVE_INFINITY;
    }

    const dLat = toRadians(destinationLat - originLat);
    const dLng = toRadians(destinationLng - originLng);
    const lat1 = toRadians(originLat);
    const lat2 = toRadians(destinationLat);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return earthRadiusKm * c;
}

function normalizeComparableCap(value) {
    const normalized = normalizeCap(value);

    if (normalized.length > 0 && normalized.length < 5) {
        return normalized.padStart(5, '0');
    }

    return normalized;
}

function getItemsMatchingCap(items, cap) {
    const targetCap = normalizeComparableCap(cap);

    if (!targetCap) {
        return [];
    }

    return items.filter(item => normalizeComparableCap(item.cap) === targetCap);
}

function formatRadiusKm(radiusKm) {
    const value = Number(radiusKm);

    if (!Number.isFinite(value)) {
        return '';
    }

    return `${Number.isInteger(value) ? value : value.toFixed(1).replace('.', ',')} km`;
}

function getNearestItemsByProgressiveRadius(items, origin, { radiiKm }) {
    const itemsWithDistance = items
        .map(item => {
            const lat = parseFloat(item.latitude);
            const lng = parseFloat(item.longitude);

            if (Number.isNaN(lat) || Number.isNaN(lng)) {
                return null;
            }

            return {
                ...item,
                _distanceKm: getDistanceKm(origin, { lat, lng }),
            };
        })
        .filter(Boolean)
        .filter(item => Number.isFinite(item._distanceKm))
        .sort((a, b) => a._distanceKm - b._distanceKm);

    for (const radiusKm of radiiKm) {
        const nearbyItems = itemsWithDistance.filter(item => item._distanceKm <= radiusKm);

        if (nearbyItems.length > 0) {
            return {
                items: nearbyItems,
                radiusKm,
                isFallbackToNearest: false,
            };
        }
    }

    return {
        items: itemsWithDistance,
        radiusKm: null,
        isFallbackToNearest: itemsWithDistance.length > 0,
    };
}

function buildCapProximityMessage(proximityResult) {
    if (!proximityResult?.items?.length) {
        return '';
    }

    if (proximityResult.radiusKm) {
        return `Nessuna struttura trovata nel CAP selezionato. Ecco le più vicine entro ${formatRadiusKm(proximityResult.radiusKm)}.`;
    }

    return 'Nessuna struttura trovata nel CAP selezionato. Ecco le più vicine disponibili.';
}

function buildUserLocationProximityMessage(proximityResult, { radiiKm }) {
    if (!proximityResult?.items?.length) {
        return '';
    }

    const firstRadiusKm = radiiKm?.[0];

    if (proximityResult.radiusKm && proximityResult.radiusKm !== firstRadiusKm) {
        return `Nessuna struttura trovata vicino a te. Ecco le più vicine entro ${formatRadiusKm(proximityResult.radiusKm)}.`;
    }

    if (!proximityResult.radiusKm && proximityResult.isFallbackToNearest) {
        return 'Nessuna struttura trovata vicino a te. Ecco le più vicine disponibili.';
    }

    return '';
}

export default function MappaStrutture({ dataUrl, geoJsonUrl, capDataUrl, title }) {
    const wrapperRef = useRef(null);
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersRef = useRef([]);
    const markerClustererRef = useRef(null);
    const markerByItemKeyRef = useRef(new Map());
    const infoWindowRef = useRef(null);
    const onRegioneClickRef = useRef(null);
    const pendingAutocompleteFocusKeyRef = useRef(null);
    const resetTimeoutRef = useRef(null);

    const [allItems, setAllItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [visibleItemsCount, setVisibleItemsCount] = useState(0);
    const [proximitySearchMessage, setProximitySearchMessage] = useState('');
    const [selectedRegione, setSelectedRegione] = useState(null);
    const [selectedProvincia, setSelectedProvincia] = useState(null);
    const [selectedTipologia, setSelectedTipologia] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [searchCap, setSearchCap] = useState('');
    const [capIndex, setCapIndex] = useState({});
    const [capLocation, setCapLocation] = useState(null);
    const [capSearchError, setCapSearchError] = useState('');
    const [isCapIndexLoaded, setIsCapIndexLoaded] = useState(false);
    const [capIndexLoadError, setCapIndexLoadError] = useState('');
    const [useMyLocation, setUseMyLocation] = useState(false);
    const [userLocation, setUserLocation] = useState(null);

    useEffect(() => {
        function updateHeight() {
            const header = document.querySelector('header');
            const headerH = header ? header.offsetHeight : 0;

            if (wrapperRef.current) {
                wrapperRef.current.style.height = `${window.innerHeight - headerH}px`;
            }
        }

        updateHeight();
        window.addEventListener('resize', updateHeight);

        return () => {
            window.removeEventListener('resize', updateHeight);

            if (resetTimeoutRef.current) {
                window.clearTimeout(resetTimeoutRef.current);
                resetTimeoutRef.current = null;
            }
        };
    }, []);

    function clearPendingMapReset() {
        if (resetTimeoutRef.current) {
            window.clearTimeout(resetTimeoutRef.current);
            resetTimeoutRef.current = null;
        }
    }

    function clearExclusiveGeoFilters() {
        setSearchCap('');
        setCapLocation(null);
        setCapSearchError('');
        setUseMyLocation(false);
        setUserLocation(null);
        setProximitySearchMessage('');
    }

    function clearFiltersExcludedByProximity() {
        setSelectedRegione(null);
        setSelectedProvincia(null);
        setSearchText('');
    }

    function clearActiveRegions() {
        const map = mapInstanceRef.current;
        if (!map) return;

        map.data.forEach(feature => {
            feature.setProperty('active', false);
            map.data.overrideStyle(feature, { fillColor: MAP_FEATURES.fillColor });
        });
    }

    function showRegionsLayer() {
        const map = mapInstanceRef.current;
        if (!map) return;

        map.data.setStyle({
            fillColor: MAP_FEATURES.fillColor,
            strokeColor: MAP_FEATURES.strokeColor,
            strokeWeight: MAP_FEATURES.strokeWeight,
            fillOpacity: MAP_FEATURES.fillOpacity,
            visible: true,
        });

        map.setOptions({
            styles: [{ featureType: 'all', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
        });
    }

    function forceInitialMapView() {
        const map = mapInstanceRef.current;
        if (!map) return;

        map.setCenter(ITALY_CENTER);
        map.setZoom(getInitialZoom());
    }

    function resetMapToInitialView({ deferred = true } = {}) {
        const map = mapInstanceRef.current;
        if (!map) return;

        clearPendingMapReset();
        clearMarkers();
        clearActiveRegions();
        showRegionsLayer();
        forceInitialMapView();

        /*
         * Il reset differito serve solo nei reset "forti", tipo annulla filtri.
         * Durante la digitazione del CAP usiamo deferred=false per evitare race condition:
         * niente vecchi timeout che riaccendono i poligoni dopo un drawMarkers().
         */
        if (!deferred) {
            return;
        }

        resetTimeoutRef.current = window.setTimeout(() => {
            showRegionsLayer();
            forceInitialMapView();
            resetTimeoutRef.current = null;
        }, 80);
    }

    function fitToRegion(regName) {
        const map = mapInstanceRef.current;
        if (!map || !regName) return;

        const bounds = new google.maps.LatLngBounds();
        let found = false;

        map.data.forEach(feature => {
            if (feature.getProperty('name') !== regName) return;

            const geometry = feature.getGeometry();

            if (!geometry || !geometry.forEachLatLng) {
                return;
            }

            geometry.forEachLatLng(latLng => {
                bounds.extend(latLng);
                found = true;
            });
        });

        if (found && !bounds.isEmpty()) {
            map.fitBounds(bounds);
        }
    }

    function highlightRegion(regName) {
        const map = mapInstanceRef.current;
        if (!map) return;

        map.data.forEach(feature => {
            feature.setProperty('active', false);
            map.data.overrideStyle(feature, { fillColor: MAP_FEATURES.fillColor });
        });

        map.data.forEach(feature => {
            if (feature.getProperty('name') === regName) {
                feature.setProperty('active', true);
                map.data.overrideStyle(feature, { fillColor: MAP_FEATURES.fillColorSelected });
            }
        });
    }

    function clearMarkers() {
        if (markerClustererRef.current) {
            markerClustererRef.current.clearMarkers();
            markerClustererRef.current.setMap?.(null);
            markerClustererRef.current = null;
        }

        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];
        markerByItemKeyRef.current.clear();

        if (infoWindowRef.current) {
            infoWindowRef.current.close();
        }
    }

    function getValidItemPosition(item) {
        const lat = parseFloat(item.latitude);
        const lng = parseFloat(item.longitude);

        if (Number.isNaN(lat) || Number.isNaN(lng)) {
            return null;
        }

        return { lat, lng };
    }

    function getInfoWindowTitle(item) {
        return getItemTitle(item);
    }

    function buildInfoWindowHeaderElement(item) {
        const title = getInfoWindowTitle(item);

        if (!title) {
            return '';
        }

        const header = document.createElement('div');
        header.className = 'infowindow-header-title';
        header.textContent = title;

        return header;
    }

    function openItemInfoWindow(item, marker) {
        const map = mapInstanceRef.current;
        if (!map || !marker || !infoWindowRef.current) return;

        const position = marker.getPosition();
        if (!position) return;

        const title = getInfoWindowTitle(item);

        infoWindowRef.current.setOptions({
            headerContent: buildInfoWindowHeaderElement(item),
            ariaLabel: title || 'Dettaglio struttura',
        });

        infoWindowRef.current.setContent(buildInfoWindowHtml(item));
        infoWindowRef.current.open(map, marker);

        map.setCenter(position);
        map.setZoom(SELECTED_ITEM_ZOOM);
    }

    onRegioneClickRef.current = regName => {
        pendingAutocompleteFocusKeyRef.current = null;
        clearExclusiveGeoFilters();

        setSelectedRegione(regName);
        setSelectedProvincia(null);
        setFilteredItems([]);
        setVisibleItemsCount(0);
        setProximitySearchMessage('');

        clearPendingMapReset();
        clearMarkers();
        showRegionsLayer();
        highlightRegion(regName);
        fitToRegion(regName);
    };

    useEffect(() => {
        let isMounted = true;

        async function fetchJson(url, label) {
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`${label}: risposta HTTP ${response.status}`);
            }

            return response.json();
        }

        async function loadCapIndex() {
            if (!capDataUrl) {
                setCapIndexLoadError('Archivio CAP non configurato.');
                setIsCapIndexLoaded(true);
                return {};
            }

            try {
                const rawCaps = await fetchJson(capDataUrl, 'Archivio CAP');
                setCapIndexLoadError('');
                return normalizeCapIndex(rawCaps);
            } catch (error) {
                console.error(error);
                setCapIndexLoadError('Archivio CAP non disponibile.');
                return {};
            } finally {
                setIsCapIndexLoaded(true);
            }
        }

        Promise.all([
            fetchJson(geoJsonUrl, 'GeoJSON regioni'),
            fetchJson(dataUrl, 'Archivio strutture'),
            loadCapIndex(),
            loadGoogleMaps(),
        ]).then(([geoJson, sportelli, normalizedCapIndex]) => {
            if (!isMounted) return;

            const sourceItems = Array.isArray(sportelli) ? sportelli : (sportelli.items || []);
            const items = sourceItems.map(normalizeItem);

            setCapIndex(normalizedCapIndex);
            setAllItems(items);
            initMap(geoJson, items);
        }).catch(error => {
            console.error(error);
        });

        return () => {
            isMounted = false;
        };
    }, [dataUrl, geoJsonUrl, capDataUrl]);

    useEffect(() => {
        if (searchCap.length !== 5) return;
        if (!isCapIndexLoaded) return;

        const location = getCapLocation(searchCap, capIndex);

        setCapLocation(location);
        setCapSearchError(location ? '' : (capIndexLoadError || 'CAP non trovato. Controlla il codice inserito.'));
    }, [capIndex, capIndexLoadError, isCapIndexLoaded, searchCap]);

    function initMap(geoJson, items) {
        const map = new google.maps.Map(mapRef.current, {
            center: ITALY_CENTER,
            zoom: getInitialZoom(),
            gestureHandling: 'cooperative',

            // Rimuove lo switch "Mappa / Satellite"
            mapTypeControl: false,

            // Rimuove il Pegman / Street View in basso a destra
            streetViewControl: false,

            // Rimuove "Controlli di visualizzazione della mappa"
            // introdotto nelle versioni più recenti della Google Maps JS API
            cameraControl: false,

            // Mantiene il bottone fullscreen in alto a destra
            fullscreenControl: true,
            fullscreenControlOptions: {
                position: google.maps.ControlPosition.RIGHT_TOP,
            },

            // Mostra i bottoni + / - sotto al fullscreen
            zoomControl: true,
            zoomControlOptions: {
                position: google.maps.ControlPosition.RIGHT_TOP,
            },

            styles: [{ featureType: 'all', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
        });

        mapInstanceRef.current = map;
        infoWindowRef.current = new google.maps.InfoWindow();

        map.data.setStyle({
            fillColor: MAP_FEATURES.fillColor,
            strokeColor: MAP_FEATURES.strokeColor,
            strokeWeight: MAP_FEATURES.strokeWeight,
            fillOpacity: MAP_FEATURES.fillOpacity,
        });

        const enriched = { ...geoJson };

        enriched.features = geoJson.features.map(feature => {
            const regionItems = items.filter(
                item => item.regione && item.regione.toLowerCase() === feature.properties.name.toLowerCase()
            );

            return {
                ...feature,
                properties: {
                    ...feature.properties,
                    items: regionItems,
                },
            };
        });

        map.data.addGeoJson(enriched, { idPropertyName: 'name' });

        map.data.addListener('click', event => {
            onRegioneClickRef.current(event.feature.getProperty('name'));
        });

        map.data.addListener('mouseover', event => {
            if (!event.feature.getProperty('active')) {
                map.data.overrideStyle(event.feature, { fillColor: MAP_FEATURES.fillColorSelected });
            }
        });

        map.data.addListener('mouseout', event => {
            if (!event.feature.getProperty('active')) {
                map.data.overrideStyle(event.feature, { fillColor: MAP_FEATURES.fillColor });
            }
        });
    }

    const handleRegioneSelect = useCallback(regione => {
        pendingAutocompleteFocusKeyRef.current = null;
        clearExclusiveGeoFilters();

        setSelectedRegione(regione || null);
        setSelectedProvincia(null);

        clearPendingMapReset();
        clearMarkers();
        showRegionsLayer();

        const map = mapInstanceRef.current;
        if (!map) return;

        if (!regione) {
            resetMapToInitialView();
            return;
        }

        highlightRegion(regione);
        fitToRegion(regione);
    }, []);

    const handleProvinciaSelect = useCallback(provincia => {
        pendingAutocompleteFocusKeyRef.current = null;
        clearExclusiveGeoFilters();

        setSelectedProvincia(provincia || null);
    }, []);

    const handleTipologiaSelect = useCallback(tipologia => {
        pendingAutocompleteFocusKeyRef.current = null;
        setSelectedTipologia(tipologia || null);
    }, []);

    const handleFreeSearch = useCallback(text => {
        pendingAutocompleteFocusKeyRef.current = null;
        clearExclusiveGeoFilters();

        setSearchText(text || '');
    }, []);

    const handleAutocompleteSelect = useCallback((item, value) => {
        if (!item) return;

        const itemKey = buildItemKey(item);
        const nextSearchText = value || getItemTitle(item) || getItemAddress(item) || '';

        pendingAutocompleteFocusKeyRef.current = itemKey;
        clearExclusiveGeoFilters();

        setSearchText(nextSearchText);

        const map = mapInstanceRef.current;

        if (map) {
            map.data.forEach(feature => feature.setProperty('active', false));
        }
    }, []);

    const handleCapSearch = useCallback(cap => {
        pendingAutocompleteFocusKeyRef.current = null;

        const normalized = normalizeCap(cap);
        setCapSearchError('');

        if (normalized.length > 0) {
            clearFiltersExcludedByProximity();
            setUseMyLocation(false);
            setUserLocation(null);

            const map = mapInstanceRef.current;

            if (map) {
                clearPendingMapReset();
                map.data.forEach(feature => feature.setProperty('active', false));
                showRegionsLayer();
            }
        }

        setSearchCap(normalized);

        if (normalized.length !== 5) {
            setCapLocation(null);

            if (normalized.length === 0) {
                resetMapToInitialView();
            }

            return;
        }

        if (!isCapIndexLoaded) {
            setCapLocation(null);
            setCapSearchError('Archivio CAP ancora in caricamento.');
            return;
        }

        const location = getCapLocation(normalized, capIndex);

        setCapLocation(location);
        setCapSearchError(location ? '' : (capIndexLoadError || 'CAP non trovato. Controlla il codice inserito.'));
    }, [capIndex, capIndexLoadError, isCapIndexLoaded]);

    const handleUseMyLocationToggle = useCallback(enabled => {
        pendingAutocompleteFocusKeyRef.current = null;

        if (enabled) {
            clearFiltersExcludedByProximity();

            setSearchCap('');
            setCapLocation(null);
            setCapSearchError('');

            const map = mapInstanceRef.current;

            if (map) {
                clearPendingMapReset();
                map.data.forEach(feature => feature.setProperty('active', false));
                showRegionsLayer();
            }
        }

        setUseMyLocation(enabled);

        if (!enabled) {
            setUserLocation(null);
            resetMapToInitialView();
            return;
        }

        if (!navigator.geolocation) {
            setUseMyLocation(false);
            setUserLocation(null);
            resetMapToInitialView();
            return;
        }

        navigator.geolocation.getCurrentPosition(
            position => {
                setUserLocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                });
            },
            () => {
                setUseMyLocation(false);
                setUserLocation(null);
                resetMapToInitialView();
            }
        );
    }, []);

    useEffect(() => {
        if (!allItems.length) {
            setFilteredItems([]);
            setVisibleItemsCount(0);
            setProximitySearchMessage('');
            return;
        }

        const hasSearchText = !!searchText.trim();
        const hasPartialCap = searchCap.length > 0 && searchCap.length < 5;
        const hasCompleteCap = searchCap.length === 5;
        const hasCapProximity = hasCompleteCap && !!capLocation;
        const hasUserLocationMode = !!useMyLocation;
        const hasUserProximity = !!(useMyLocation && userLocation);
        const hasSelectedTipologia = !!selectedTipologia;
        const hasConcatenableFilters = !!(selectedRegione || selectedProvincia || hasSelectedTipologia || hasSearchText);
        const shouldShowResults = hasConcatenableFilters || hasPartialCap || hasCompleteCap || hasUserLocationMode;
        const itemsMatchingTipologia = hasSelectedTipologia
            ? allItems.filter(item => getStructureType(item).toLowerCase() === selectedTipologia.toLowerCase())
            : allItems;

        if (!shouldShowResults) {
            setFilteredItems([]);
            setVisibleItemsCount(0);
            setProximitySearchMessage('');
            resetMapToInitialView();
            return;
        }

        if (hasUserLocationMode) {
            if (!hasUserProximity) {
                setFilteredItems([]);
                setVisibleItemsCount(0);
                setProximitySearchMessage('');
                resetMapToInitialView({ deferred: false });
                return;
            }

            const proximityResult = getNearestItemsByProgressiveRadius(
                itemsMatchingTipologia,
                userLocation,
                PROXIMITY_SEARCH_CONFIG.userLocation
            );
            const items = proximityResult.items;

            setProximitySearchMessage(
                buildUserLocationProximityMessage(proximityResult, PROXIMITY_SEARCH_CONFIG.userLocation)
            );
            setFilteredItems(items);
            drawMarkers(items);
            return;
        }

        if (hasPartialCap) {
            setFilteredItems([]);
            setVisibleItemsCount(0);
            setProximitySearchMessage('');

            /*
             * CAP parziale: torno subito alla vista iniziale, ma senza timeout.
             * Questo risolve il caso:
             * CAP completo -> marker/zoom in -> cancello una cifra -> poligoni + zoom out.
             * E non reintroduce la race condition dei vecchi reset differiti.
             */
            resetMapToInitialView({ deferred: false });
            return;
        }

        if (hasCompleteCap) {
            if (!hasCapProximity) {
                setFilteredItems([]);
                setVisibleItemsCount(0);
                setProximitySearchMessage('');
                resetMapToInitialView({ deferred: false });
                return;
            }

            const exactCapItems = getItemsMatchingCap(itemsMatchingTipologia, searchCap);

            if (exactCapItems.length > 0) {
                setProximitySearchMessage('');
                setFilteredItems(exactCapItems);
                drawMarkers(exactCapItems);
                return;
            }

            const proximityResult = getNearestItemsByProgressiveRadius(
                itemsMatchingTipologia,
                capLocation,
                PROXIMITY_SEARCH_CONFIG.cap
            );
            const items = proximityResult.items;

            setProximitySearchMessage(buildCapProximityMessage(proximityResult));
            setFilteredItems(items);
            drawMarkers(items);
            return;
        }

        let items = allItems.filter(item => {
            if (
                selectedRegione &&
                (!item.regione || item.regione.toLowerCase() !== selectedRegione.toLowerCase())
            ) {
                return false;
            }

            if (
                selectedProvincia &&
                (!item.provincia || item.provincia.toLowerCase() !== selectedProvincia.toLowerCase())
            ) {
                return false;
            }

            if (
                selectedTipologia &&
                getStructureType(item).toLowerCase() !== selectedTipologia.toLowerCase()
            ) {
                return false;
            }

            return true;
        });

        if (hasSearchText) {
            const lower = searchText.trim().toLowerCase();

            items = items.filter(item => {
                const denominazione = (item.denominazione || '').toLowerCase();
                const indirizzo = getItemAddress(item).toLowerCase();

                return (
                    denominazione.includes(lower) ||
                    indirizzo.includes(lower)
                );
            });
        }

        setProximitySearchMessage('');
        setFilteredItems(items);
        drawMarkers(items);
    }, [
        allItems,
        selectedRegione,
        selectedProvincia,
        selectedTipologia,
        searchText,
        searchCap,
        capLocation,
        useMyLocation,
        userLocation,
    ]);

    function drawMarkers(items) {
        clearPendingMapReset();
        clearMarkers();

        const map = mapInstanceRef.current;

        if (!map) {
            setVisibleItemsCount(0);
            return;
        }

        const drawableItems = items
            .map(item => ({ item, position: getValidItemPosition(item) }))
            .filter(({ position }) => position);

        setVisibleItemsCount(drawableItems.length);

        if (!drawableItems.length) {
            resetMapToInitialView({ deferred: false });
            return;
        }

        map.setOptions({ styles: [{ stylers: [{ visibility: 'on' }] }] });
        map.data.setStyle({ visible: false });

        const bounds = new google.maps.LatLngBounds();

        drawableItems.forEach(({ item, position }) => {
            const pos = new google.maps.LatLng(position.lat, position.lng);
            bounds.extend(pos);

            const marker = new google.maps.Marker({
                position: pos,
                map: MARKER_CLUSTER_CONFIG.enabled ? null : map,
                icon: '/Content/img/marker.png',
                title: getItemTitle(item),
            });

            const itemKey = buildItemKey(item);
            markerByItemKeyRef.current.set(itemKey, marker);

            marker.addListener('click', () => {
                openItemInfoWindow(item, marker);
            });

            markersRef.current.push(marker);
        });

        if (MARKER_CLUSTER_CONFIG.enabled && markersRef.current.length) {
            markerClustererRef.current = new MarkerClusterer({
                map,
                markers: markersRef.current,
                algorithm: new SuperClusterAlgorithm({
                    maxZoom: MARKER_CLUSTER_CONFIG.maxZoom,
                    radius: MARKER_CLUSTER_CONFIG.radius,
                    minPoints: MARKER_CLUSTER_CONFIG.minPoints,
                }),
                renderer: createClusterRenderer(),
            });
        }

        const pendingFocusKey = pendingAutocompleteFocusKeyRef.current;

        if (pendingFocusKey) {
            const itemToFocus = items.find(item => buildItemKey(item) === pendingFocusKey);
            const markerToFocus = markerByItemKeyRef.current.get(pendingFocusKey);

            pendingAutocompleteFocusKeyRef.current = null;

            if (itemToFocus && markerToFocus) {
                openItemInfoWindow(itemToFocus, markerToFocus);
                return;
            }
        }

        if (!bounds.isEmpty()) {
            map.fitBounds(bounds);
        }
    }

    function buildInfoWindowHtml(item) {
        const tipologiaStruttura = getStructureType(item);
        const indirizzo = item['indirizzo completo'] || item.indirizzo || '';
        const citta = item['città'] || item.citta || '';
        const provincia = item.provincia || '';
        const telefono = item.telefono || '';
        const email = item.email || '';
        const orari = item.orari || '';
        //const distanza = formatDistanceKm(item._distanceKm);
        const emailLinks = buildEmailLinks(email);
        const orariHtml = escapeHtml(orari).replace(/([0-9])\s([a-zA-Z])/ig, '$1<br />$2');
        const indirizzoHtml = `${escapeHtml(indirizzo)}${citta ? `, ${escapeHtml(citta)}` : ''}`;
        ////${distanza ? `<div><span class="label">Distanza:</span><span>${escapeHtml(distanza)}</span></div>` : ''}

        return `<div class="infowindow p-3">
            <div class="info">
                ${tipologiaStruttura ? `<div><span class="label">Tipologia:</span><span>${escapeHtml(tipologiaStruttura)}</span></div>` : ''}
                ${indirizzo ? `<div><span class="label">Indirizzo:</span><span>${indirizzoHtml}</span></div>` : ''}
                ${provincia ? `<div><span class="label">Provincia:</span><span>${escapeHtml(provincia)}</span></div>` : ''}
                ${telefono ? `<div><span class="label">Telefono:</span><span><a title="Chiama ${escapeHtml(telefono)}" href="tel:${escapeHtml(telefono)}">${escapeHtml(telefono)}</a></span></div>` : ''}
                ${emailLinks ? `<div><span class="label">Email:</span><span>${emailLinks}</span></div>` : ''}
                ${orari ? `<div><span class="label">Orari:</span><span>${orariHtml}</span></div>` : ''}
            </div>
        </div>`;
    }

    const handleReset = useCallback(() => {
        pendingAutocompleteFocusKeyRef.current = null;

        setSelectedRegione(null);
        setSelectedProvincia(null);
        setSelectedTipologia(null);
        setFilteredItems([]);
        setVisibleItemsCount(0);
        setProximitySearchMessage('');
        setSearchText('');
        setSearchCap('');
        setCapLocation(null);
        setCapSearchError('');
        setUseMyLocation(false);
        setUserLocation(null);

        resetMapToInitialView();
    }, []);

    const regioni = useMemo(
        () => [...new Set(allItems.filter(item => item.regione).map(item => item.regione))].sort(),
        [allItems]
    );

    const province = useMemo(
        () => selectedRegione
            ? [...new Set(
                allItems
                    .filter(item =>
                        item.regione &&
                        item.regione.toLowerCase() === selectedRegione.toLowerCase() &&
                        item.provincia
                    )
                    .map(item => item.provincia)
            )].sort()
            : [],
        [allItems, selectedRegione]
    );

    const tipologie = useMemo(() => {
        let items = allItems;

        if (selectedRegione) {
            items = items.filter(item =>
                item.regione &&
                item.regione.toLowerCase() === selectedRegione.toLowerCase()
            );
        }

        if (selectedProvincia) {
            items = items.filter(item =>
                item.provincia &&
                item.provincia.toLowerCase() === selectedProvincia.toLowerCase()
            );
        }

        return [...new Set(items.map(getStructureType).filter(Boolean))].sort();
    }, [allItems, selectedRegione, selectedProvincia]);

    return (
        <div className="ms-map-wrapper" ref={wrapperRef}>
            <Sidebar
                title={title}
                allItems={allItems}
                selectedRegione={selectedRegione}
                selectedProvincia={selectedProvincia}
                selectedTipologia={selectedTipologia}
                regioni={regioni}
                province={province}
                tipologie={tipologie}
                searchText={searchText}
                searchCap={searchCap}
                capSearchError={capSearchError}
                isCapIndexLoaded={isCapIndexLoaded}
                useMyLocation={useMyLocation}
                filteredItems={filteredItems}
                visibleItemsCount={visibleItemsCount}
                proximitySearchMessage={proximitySearchMessage}
                onRegioneSelect={handleRegioneSelect}
                onTipologiaSelect={handleTipologiaSelect}
                onFreeSearch={handleFreeSearch}
                onAutocompleteSelect={handleAutocompleteSelect}
                onCapSearch={handleCapSearch}
                onUseMyLocationToggle={handleUseMyLocationToggle}
                onProvinciaSelect={handleProvinciaSelect}
                onReset={handleReset}
            />

            <div className="ms-map-canvas" ref={mapRef} />
        </div>
    );
}